import React, { useState, useEffect } from 'react';
import { Avatar, Tooltip, Dropdown, Divider, App, Switch, Spin } from 'antd';
import { motion } from 'framer-motion';
import {
  PlusOutlined,
  MessageOutlined,
  FolderOutlined,
  BarChartOutlined,
  SettingOutlined,
  UserOutlined,
  GlobalOutlined,
  QuestionCircleOutlined,
  CrownOutlined,
  LogoutOutlined,
  CheckOutlined,
  BulbOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as SplitViewIcon } from '../../assets/svgs/split-two.svg';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { useChatManager } from '../../hooks/useChatManager';
import { logout as logoutApi } from '../../services/api/authService';
import { getUserChats } from '../../services/api/chatService';
import { UserChat } from '../../types';

interface SidebarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
  isMobile?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onCollapse, isMobile = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { mode, toggleTheme } = useTheme();
  const { chats, setCurrentChatId, clearCurrentChat } = useChatManager();
  const { notification } = App.useApp();
  const [profileDropdownVisible, setProfileDropdownVisible] = useState(false);
  const [showSplitIcon, setShowSplitIcon] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [recentChats, setRecentChats] = useState<UserChat[]>([]);
  const [loadingChats, setLoadingChats] = useState(false);

  // Theme-aware colors
  const colors = {
    bg: mode === 'dark' ? '#0F0F10' : '#FFFFFF',
    bgHover: mode === 'dark' ? '#1A1A1B' : '#F3F4F6',
    bgSelected: mode === 'dark' ? '#2563EB' : '#EEF2FF',
    border: mode === 'dark' ? '#2A2A2A' : 'rgba(0, 0, 0, 0.08)',
    text: mode === 'dark' ? '#ECECEC' : '#1A1A1A',
    textSecondary: mode === 'dark' ? '#B8B8B8' : '#6B7280',
    textMuted: mode === 'dark' ? '#8A8A8A' : '#9CA3AF',
    dropdownBg: mode === 'dark' ? '#2F2F2F' : '#FFFFFF',
    dropdownBorder: mode === 'dark' ? '#404040' : 'rgba(0, 0, 0, 0.12)',
    dropdownHover: mode === 'dark' ? '#404040' : '#F3F4F6'
  };

  // Fetch user's recent chats from API
  useEffect(() => {
    const fetchRecentChats = async () => {
      if (!user?.id) return;

      try {
        setLoadingChats(true);
        const response = await getUserChats(user.id, 10);
        setRecentChats(response.chats);
      } catch (error) {
        console.error('❌ Failed to fetch recent chats:', error);
        // Silently fail and show empty state
        setRecentChats([]);
      } finally {
        setLoadingChats(false);
      }
    };

    fetchRecentChats();
  }, [user?.id]);

  const menuItems = [
    {
      key: '/new-chat',
      icon: <PlusOutlined />,
      label: 'New Chat',
      title: 'Start a new conversation',
      special: true
    },
    {
      key: '/chats',
      icon: <MessageOutlined />,
      label: 'Chats',
      title: 'View conversation history'
    },
    {
      key: '/projects/create',
      icon: <FolderOutlined />,
      label: 'Projects',
      title: 'Project management'
    },
    {
      key: '/reports',
      icon: <BarChartOutlined />,
      label: 'Reports',
      title: 'Analytics & Reports'
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      title: 'System Settings'
    }
  ];

  const handleMenuClick = (key: string) => {
    // Clear current chat when clicking "New Chat"
    if (key === '/new-chat' || key === '/') {
      clearCurrentChat();
    }
    navigate(key);
  };

  const handleChatClick = (chatId: string) => {
    setCurrentChatId(chatId);
    navigate(`/new-chat/${chatId}`);
  };

  const truncateTitle = (title: string, maxLength: number = 30) => {
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength - 3) + '...';
  };

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      setProfileDropdownVisible(false);

      console.log('🚪 Logging out...');

      // Call logout API
      await logoutApi();

      // Clear local auth state
      logout();

      notification.success({
        message: 'Logged Out',
        description: 'You have been successfully logged out.',
        placement: 'topRight',
        duration: 3,
      });

      // Redirect to login
      navigate('/login');
    } catch (error) {
      console.error('❌ Logout error:', error);

      // Even if API call fails, clear local state and redirect
      logout();
      navigate('/login');

      notification.warning({
        message: 'Logged Out',
        description: 'Session cleared successfully.',
        placement: 'topRight',
        duration: 3,
      });
    } finally {
      setLoggingOut(false);
    }
  };

  const profileDropdownContent = (
    <div style={{
      background: '#2F2F2F',
      border: '1px solid #404040',
      borderRadius: '8px',
      padding: '8px',
      minWidth: '240px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
    }}>
      {/* User Info Section */}
      <div style={{
        padding: '12px',
        borderRadius: '6px',
        background: '#3A3A3A',
        marginBottom: '8px'
      }}>
        <div style={{
          color: '#8A8A8A',
          fontSize: '13px',
          marginBottom: '4px'
        }}>
          {user?.email || 'No email'}
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '2px'
            }}>
              <div style={{
                width: '16px',
                height: '16px',
                background: '#2563EB',
                borderRadius: '2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: 'white',
                fontWeight: 'bold'
              }}>
                {user?.firstName?.charAt(0).toUpperCase() || user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <span style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: '500' }}>
                {user?.orgName || user?.department || 'Organization'}
              </span>
            </div>
            <div style={{ color: '#8A8A8A', fontSize: '12px' }}>
              {user?.roleName || user?.role || 'User'}
            </div>
          </div>
          <CheckOutlined style={{ color: '#2563EB', fontSize: '16px' }} />
        </div>
      </div>

      {/* Menu Items */}
      <div style={{ padding: '4px 0' }}>
        <div style={{ color: colors.textMuted, fontSize: '11px', padding: '8px 12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Settings
        </div>

        {/* Theme Toggle */}
        <motion.div
          style={{
            padding: '8px 12px',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
          whileHover={{ background: colors.dropdownHover }}
          onClick={() => {
            toggleTheme();
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <BulbOutlined style={{ color: colors.textSecondary, fontSize: '16px' }} />
            <span style={{ color: colors.text, fontSize: '14px' }}>
              {mode === 'dark' ? 'Dark Mode' : 'Light Mode'}
            </span>
          </div>
          <Switch
            size="small"
            checked={mode === 'light'}
            onChange={(checked) => {
              toggleTheme();
            }}
          />
        </motion.div>

        <motion.div
          style={{
            padding: '8px 12px',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
          whileHover={{ background: colors.dropdownHover }}
          onClick={() => {
            setProfileDropdownVisible(false);
            // Handle language click
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <GlobalOutlined style={{ color: colors.textSecondary, fontSize: '16px' }} />
            <span style={{ color: colors.text, fontSize: '14px' }}>Language</span>
          </div>
          <span style={{ color: colors.textMuted }}>→</span>
        </motion.div>

        <motion.div
          style={{
            padding: '8px 12px',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          whileHover={{ background: '#404040' }}
          onClick={() => {
            setProfileDropdownVisible(false);
            // Handle get help click
          }}
        >
          <QuestionCircleOutlined style={{ color: '#B8B8B8', fontSize: '16px' }} />
          <span style={{ color: '#FFFFFF', fontSize: '14px' }}>Get help</span>
        </motion.div>

        <Divider style={{ 
          margin: '8px 0', 
          borderColor: '#404040',
          borderWidth: '0.5px'
        }} />

        <motion.div
          style={{
            padding: '8px 12px',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          whileHover={{ background: '#404040' }}
          onClick={() => {
            setProfileDropdownVisible(false);
            // Handle upgrade plan click
          }}
        >
          <CrownOutlined style={{ color: '#B8B8B8', fontSize: '16px' }} />
          <span style={{ color: '#FFFFFF', fontSize: '14px' }}>Upgrade plan</span>
        </motion.div>

        <motion.div
          style={{
            padding: '8px 12px',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
          whileHover={{ background: '#404040' }}
          onClick={() => {
            setProfileDropdownVisible(false);
            // Handle learn more click
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ color: '#FFFFFF', fontSize: '14px' }}>Learn more</span>
          </div>
          <span style={{ color: '#8A8A8A' }}>→</span>
        </motion.div>

        <Divider style={{ 
          margin: '8px 0', 
          borderColor: '#404040',
          borderWidth: '0.5px'
        }} />

        <motion.div
          style={{
            padding: '8px 12px',
            borderRadius: '4px',
            cursor: loggingOut ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            opacity: loggingOut ? 0.6 : 1
          }}
          whileHover={{ background: loggingOut ? 'transparent' : '#404040' }}
          onClick={loggingOut ? undefined : handleLogout}
        >
          <LogoutOutlined style={{ color: '#B8B8B8', fontSize: '16px' }} />
          <span style={{ color: '#FFFFFF', fontSize: '14px' }}>
            {loggingOut ? 'Logging out...' : 'Log out'}
          </span>
        </motion.div>
      </div>
    </div>
  );

  return (
    <div
      style={{
        width: isMobile ? (collapsed ? '0' : '280px') : (collapsed ? '60px' : '280px'),
        height: '100vh',
        position: 'fixed',
        left: isMobile && collapsed ? '-280px' : '0',
        top: 0,
        zIndex: 100,
        background: colors.bg,
        backdropFilter: mode === 'dark' ? 'blur(20px)' : 'none',
        borderRight: `1px solid ${colors.border}`,
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isMobile && !collapsed ? 'translateX(0)' : isMobile ? 'translateX(-280px)' : 'translateX(0)',
        boxShadow: mode === 'light' ? '2px 0 8px rgba(0, 0, 0, 0.05)' : (isMobile && !collapsed ? '4px 0 24px rgba(0, 0, 0, 0.5)' : 'none')
      }}
    >
      {/* Top Section */}
      <div style={{
        padding: '12px',
        borderBottom: `1px solid ${colors.border}`,
        position: 'relative'
      }}>

        {/* Logo Section */}
        <motion.div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            marginBottom: '16px',
            paddingBottom: '16px',
            borderBottom: '1px solid #2c2f2f',
            paddingLeft: collapsed ? '0' : '12px',
            paddingRight: collapsed ? '0' : '12px',
            position: 'relative',
            minHeight: '44px'
          }}
          onHoverStart={() => setShowSplitIcon(true)}
          onHoverEnd={() => setShowSplitIcon(false)}
        >
          {/* Logo - hidden when collapsed and hovering */}
          {!(collapsed && showSplitIcon) && (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <motion.img
                src="https://procurexuatstg.blob.core.windows.net/procurexuatblob/prc7.png"
                alt="Logo"
                style={{
                  height: '28px',
                  width: 'auto'
                }}
                initial={{ opacity: 1 }}
                animate={{ opacity: (collapsed && showSplitIcon) ? 0 : 1 }}
                transition={{ duration: 0.2 }}
              />
              {/* BETA Badge */}
              {!collapsed && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-30px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    color: '#FFFFFF',
                    fontSize: '9px',
                    fontWeight: '700',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    letterSpacing: '0.5px',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}
                >
                  BETA
                </motion.div>
              )}
            </div>
          )}

          {/* Split View Icon */}
          {(collapsed && showSplitIcon) && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onCollapse(!collapsed)}
              style={{ cursor: 'pointer' }}
              title="Expand"
            >
              <SplitViewIcon style={{
                width: '26px',
                height: '26px',
                fill: '#B8B8B8'
              }} />
            </motion.div>
          )}

          {/* Split View Icon - always visible when expanded */}
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2, delay: 0.1 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onCollapse(!collapsed)}
              style={{ cursor: 'pointer' }}
              title="Collapse"
            >
              <SplitViewIcon style={{
                width: '26px',
                height: '26px',
                fill: '#B8B8B8'
              }} />
            </motion.div>
          )}
        </motion.div>

        {/* Toggle moved to bottom */}

        {/* New Chat Button */}
        <Tooltip title="New Chat" placement="right" open={collapsed ? undefined : false}>
          <motion.div
            style={{
              width: '100%',
              height: '36px',
              background: location.pathname === '/new-chat' || location.pathname === '/' ?
                'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)' :
                'rgba(44, 47, 47, 0.6)',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              cursor: 'pointer',
              padding: '0 12px',
              marginTop: '0px',
              position: 'relative',
              boxShadow: (location.pathname === '/new-chat' || location.pathname === '/') ?
                '0 0 15px rgba(59, 130, 246, 0.4)' : 'none'
            }}
            whileHover={{ background: (location.pathname === '/new-chat' || location.pathname === '/') ?
              'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)' :
              'rgba(44, 47, 47, 0.8)' }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleMenuClick('/new-chat')}
          >
            <PlusOutlined style={{ color: '#FFFFFF', fontSize: '16px' }} />
            <span style={{ 
              color: '#FFFFFF', 
              fontSize: '14px',
              marginLeft: '12px',
              opacity: collapsed ? 0 : 1,
              transition: 'opacity 0.2s ease-in-out',
              transitionDelay: collapsed ? '0s' : '0.1s'
            }}>
              New Chat
            </span>
          </motion.div>
        </Tooltip>
      </div>

      {/* Main Menu */}
      <div style={{
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        flexShrink: 0
      }}>
        {menuItems.slice(1).map(item => (
          <Tooltip key={item.key} title={item.label} placement="right" open={collapsed ? undefined : false}>
            <motion.div
              style={{
                width: '100%',
                height: '36px',
                background: location.pathname === item.key ? '#2c2f2f' : 'transparent',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                cursor: 'pointer',
                padding: '0 12px',
                position: 'relative'
              }}
              whileHover={{ background: '#2c2f2f' }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleMenuClick(item.key)}
            >
              {React.cloneElement(item.icon, {
                style: { color: '#B8B8B8', fontSize: '16px' }
              })}
              <span style={{
                color: '#B8B8B8',
                fontSize: '14px',
                marginLeft: '12px',
                opacity: collapsed ? 0 : 1,
                transition: 'opacity 0.2s ease-in-out',
                transitionDelay: collapsed ? '0s' : '0.1s'
              }}>
                {item.label}
              </span>
            </motion.div>
          </Tooltip>
        ))}
      </div>

      {/* Recent Chats Section */}
      {!collapsed && (
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: '0 12px'
        }}>
          <div style={{
            color: '#8A8A8A',
            fontSize: '11px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            padding: '12px 0 8px 12px',
            flexShrink: 0
          }}>
            Recent Chats
          </div>
          <motion.div
            style={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              paddingRight: '4px'
            }}
            className="hide-scrollbar"
          >
            {loadingChats ? (
              <div style={{
                padding: '20px 12px',
                textAlign: 'center',
                color: colors.textMuted,
                fontSize: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 18, color: colors.textSecondary }} spin />} />
                <span>Loading chats...</span>
              </div>
            ) : recentChats.length > 0 ? (
              recentChats.map((chat) => (
                <motion.div
                  key={chat.chat_id}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    background: 'transparent',
                    color: colors.textSecondary,
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                  whileHover={{ background: colors.bgHover }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleChatClick(chat.chat_id)}
                >
                  {truncateTitle(chat.title)}
                </motion.div>
              ))
            ) : (
              <div style={{
                padding: '20px 12px',
                textAlign: 'center',
                color: colors.textMuted,
                fontSize: '12px'
              }}>
                No chats yet
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Bottom Section: User */}
      <div style={{
        padding: '12px',
        borderTop: '1px solid #404040',
        marginTop: 'auto'
      }}>
        <Dropdown
          dropdownRender={() => profileDropdownContent}
          trigger={['click']}
          placement="topLeft"
          open={profileDropdownVisible}
          onOpenChange={setProfileDropdownVisible}
        >
          <Tooltip title="User Profile" placement="right" open={collapsed ? undefined : false}>
            <motion.div
              style={{
                width: collapsed ? '36px' : '100%',
                height: '36px',
                background: 'transparent',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                cursor: 'pointer',
                padding: collapsed ? '0' : '0 12px',
                gap: '12px',
                margin: collapsed ? '0 auto' : '0'
              }}
              whileHover={{ background: '#404040' }}
              whileTap={{ scale: 0.95 }}
            >
              <Avatar
                size="small"
                icon={<UserOutlined />}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  boxShadow: '0 0 10px rgba(59, 130, 246, 0.3)',
                  flexShrink: 0
                }}
              />
              {!collapsed && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                >
                  <span style={{
                    color: '#FFFFFF',
                    fontSize: '13px',
                    lineHeight: '1.2',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {user?.firstName || user?.name || 'User'}
                  </span>
                  <span style={{
                    color: '#8A8A8A',
                    fontSize: '11px',
                    lineHeight: '1.2',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {user?.roleName || user?.role || 'Member'}
                  </span>
                </motion.div>
              )}
            </motion.div>
          </Tooltip>
        </Dropdown>
      </div>

      {/* Overlay Toggle Button on right border near bottom */}
      {/* <Tooltip title={collapsed ? 'Expand' : 'Collapse'} placement="right" open={collapsed ? undefined : false}>
        <motion.div
          style={{
            position: 'absolute',
            right: '-15px',
            bottom: '45px',
            width: '30px',
            height: '30px',
            background: '#1f2121',
            border: '1px solid #2c2f2f',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 101
          }}
          whileHover={{ background: '#404040' }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onCollapse(!collapsed)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <RightCircleOutlined style={{ color: '#B8B8B8', fontSize: '18px' }} />
          ) : (
            <LeftCircleOutlined style={{ color: '#B8B8B8', fontSize: '18px' }} />
          )}
        </motion.div>
      </Tooltip> */}
    </div>
  );
};

export default Sidebar;
